export interface ViolationDetail {
  violationType: string;
  // Shared per-violation
  perpetratorKnown: boolean;
  perpetratorTypes: string[];
  perpetratorDetails: string;
  legalCaseFiled: boolean | null;
  legalCaseDetails: string;
  legalCaseWhyNot: string;
  stateActionUndertaken: boolean | null;
  stateActionDetails: string;
  stateActionWhyNot: string;
  // Criminalization-specific
  whyVictimsCriminalized: string;
  harassmentPrior: boolean | null;
  harassmentPriorDetails: string;
  accusations: string;
  policiesOrLawsUsed: string;
  whoFiledAccusations: string[];
  whoFiledAccusationsDetails: string;
  accusationsLegallyFiled: boolean | null;
  courtFilingLocation: string;
  courtFilingDetails: string;
  decisionOnAccusations: boolean | null;
  decisionDetails: string;
  victimsInPrison: boolean | null;
  prisonConditions: string;
  victimsSituationIfNotInPrison: string;
  // Killing-specific
  whyVictimsKilled: string;
  howKillingCarriedOut: string;
  // Other-specific
  cause: string;
}

export const createEmptyViolationDetail = (violationType: string): ViolationDetail => ({
  violationType,
  perpetratorKnown: false,
  perpetratorTypes: [],
  perpetratorDetails: '',
  legalCaseFiled: null,
  legalCaseDetails: '',
  legalCaseWhyNot: '',
  stateActionUndertaken: null,
  stateActionDetails: '',
  stateActionWhyNot: '',
  whyVictimsCriminalized: '',
  harassmentPrior: null,
  harassmentPriorDetails: '',
  accusations: '',
  policiesOrLawsUsed: '',
  whoFiledAccusations: [],
  whoFiledAccusationsDetails: '',
  accusationsLegallyFiled: null,
  courtFilingLocation: '',
  courtFilingDetails: '',
  decisionOnAccusations: null,
  decisionDetails: '',
  victimsInPrison: null,
  prisonConditions: '',
  victimsSituationIfNotInPrison: '',
  whyVictimsKilled: '',
  howKillingCarriedOut: '',
  cause: '',
});

export interface CaseFormData {
  // Documenter Details
  documentorName: string;
  documentorOrganization: string;
  documentorEmail: string;
  documentorPhone: string;
  isPrimaryData: boolean;
  dataSource: string;
  dataUsageRestriction: string;
  willingToShareMoreInfo: boolean;
  isLDSFApplicant: boolean;
  ldsfPaymentCompletedDate: string;
  sourceInformation: string;

  // Incident Details
  incidentTime: string;
  description: string;
  violationTypes: string[];
  otherViolationTypes: string[];
  otherViolationTypeDetails: string;
  isCaseOngoing: boolean;
  caseStartDate: string;
  caseStartDatePrecision: 'DAY' | 'MONTH' | 'YEAR';
  caseEndDate: string;
  reportedDate: string;

  // Location
  country: string;
  region: string;
  city: string;
  specificLocation: string;
  latitude: string;
  longitude: string;

  // Indigenous Group
  indigenousGroup: string[];
  indigenousTerritory: string;

  // Victims
  victims: Array<{
    id?: string;
    isGroup: boolean;
    // Which violation(s) this victim was subjected to. Flat keys: a top-level
    // type (KILLING/CRIMINALIZATION) or an other-type value (e.g. ARBITRARY_DETENTION).
    violationTags?: string[];
    // GROUP victims only: per-violation detail keyed by violation key -> { affectedCount
    // plus optional gender/age counts }. Blank field = not disaggregated (unknown).
    violationDemographics?: { [key: string]: { [field: string]: number } };
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    ageBracket?: string;
    gender?: string;
    dependentType?: string;
    dependentDetails?: string;
    communityRole?: string;
    indigenousGroupOfVictim?: string;
    additionalInfo?: string;
    // Group victim fields
    groupName?: string[];
    groupSize?: number;
    affectedIndividualsCount?: number;
    affectedFamiliesCount?: number;
    affectedHomesCount?: number;
    affectedGroupsCount?: number;
    affectedCommunitiesCount?: number;
    affectedVillagesCount?: number;
    affectedMaleCount?: number;
    affectedFemaleCount?: number;
    affected0to12Count?: number;
    affected13to25Count?: number;
    affected26to35Count?: number;
    affected36to59Count?: number;
    affected60PlusCount?: number;
  }>;

  // Perpetrators
  perpetrators: Array<{
    id?: string;
    isOrganization: boolean;
    name: string;
    type: string;
    organizationHead?: string;
    affiliation?: string;
    position?: string;
    notes?: string;
  }>;

  // Violation Details (per-violation-type)
  violationDetails: ViolationDetail[];

  // Impact (case-level)
  familyImpact: string;
  communityImpact: string;

  // Investigation & Advocacy (case-level)
  investigationConducted: boolean;
  investigationDetails: string;
  investigationWhyNot: string;
  advocacyUndertaken: boolean;
  advocacyDetails: string;
  advocacyWhyNot: string;

  // Development Project
  isRelatedToDevelopmentProject: boolean;
  developmentProjectType: string;
  developmentProjectName: string;
  developmentProjectDescription: string;
  developmentProjectStartYear: string;
  developmentProjectSource: string;
  developmentProjectWebsiteLink: string;

  // Metadata
  priority: number;
  tags: string[];
  isPublic: boolean;

  // Files
  files: CaseFile[];
}

export interface CaseFile {
  file: File;
  category: string;
  description: string;
  documentDate: string;
  authorSource: string;
  language: string;
  confidentiality: string;
  sensitivityWarnings: string[];
}

export const SENSITIVITY_FLAGS = [
  { value: 'GRAPHIC_CONTENT', label: 'Graphic images of injuries, death, or violence', prefix: '[GRAPHIC CONTENT]' },
  { value: 'SEXUAL_VIOLENCE', label: 'Content depicting or describing sexual violence', prefix: '[SENSITIVE - SEXUAL VIOLENCE]' },
  { value: 'MINORS', label: 'Content involving minors (under 18)', prefix: '[SENSITIVE - MINORS]' },
  { value: 'DISTRESSING', label: 'Content showing identifiable victims in distressing situations or that could cause retraumatization', prefix: '[DISTRESSING CONTENT]' },
];

export const initialFormData: CaseFormData = {
  documentorName: '',
  documentorOrganization: '',
  documentorEmail: '',
  documentorPhone: '',
  isPrimaryData: true,
  dataSource: '',
  dataUsageRestriction: '',
  willingToShareMoreInfo: false,
  isLDSFApplicant: false,
  ldsfPaymentCompletedDate: '',
  sourceInformation: '',
  incidentTime: '',
  description: '',
  violationTypes: [],
  otherViolationTypes: [],
  otherViolationTypeDetails: '',
  isCaseOngoing: false,
  caseStartDate: '',
  caseStartDatePrecision: 'DAY',
  caseEndDate: '',
  reportedDate: '',
  country: '',
  region: '',
  city: '',
  specificLocation: '',
  latitude: '',
  longitude: '',
  indigenousGroup: [],
  indigenousTerritory: '',
  victims: [],
  perpetrators: [{ isOrganization: true, name: '', type: '' }],
  violationDetails: [],
  familyImpact: '',
  communityImpact: '',
  investigationConducted: false,
  investigationDetails: '',
  investigationWhyNot: '',
  advocacyUndertaken: false,
  advocacyDetails: '',
  advocacyWhyNot: '',
  isRelatedToDevelopmentProject: false,
  developmentProjectType: '',
  developmentProjectName: '',
  developmentProjectDescription: '',
  developmentProjectStartYear: '',
  developmentProjectSource: '',
  developmentProjectWebsiteLink: '',
  priority: 1,
  tags: [],
  isPublic: false,
  files: []
};

export const VIOLATION_TYPES = [
  'KILLING',
  'CRIMINALIZATION',
  'OTHER'
];

export const PERPETRATOR_TYPE_GROUPS = [
  {
    label: 'State Actors',
    types: [
      { value: 'NATIONAL_GOVERNMENT_OFFICIAL', label: 'National Government Official' },
      { value: 'FEDERAL_GOVERNMENT_OFFICIAL', label: 'Federal Government Official' },
      { value: 'LOCAL_GOVERNMENT_OFFICIAL', label: 'Local Government Official' },
      { value: 'JUDICIARY_OFFICIAL', label: 'Judiciary Official' },
      { value: 'PRISON_OFFICIAL', label: 'Prison Official' },
      { value: 'POLICE', label: 'Police' },
      { value: 'ARMED_FORCES', label: 'Armed Forces' },
      { value: 'INTELLIGENCE_AGENCY', label: 'Intelligence Agency' },
      { value: 'FOREST_OFFICIAL', label: 'Forest Official' },
    ],
  },
  {
    label: 'Non-State Actors',
    types: [
      { value: 'VIGILANTE_GROUPS', label: 'Vigilante Groups' },
      { value: 'GUERILLA_ARMIES_OFFICIALS', label: 'Guerilla Armies Officials' },
      { value: 'PRIVATE_INDIVIDUAL', label: 'Private Individual' },
      { value: 'ORGANIZED_CRIME', label: 'Organized Crime' },
      { value: 'RELIGIOUS_INSTITUTION', label: 'Religious Institution' },
      { value: 'PRIVATE_SECURITY', label: 'Private Security' },
    ],
  },
  {
    label: 'Business / Institutional',
    types: [
      { value: 'NATIONAL_LOCAL_CORPORATION', label: 'National / Local Corporation' },
      { value: 'TRANSNATIONAL_CORPORATION', label: 'Transnational Corporation' },
      { value: 'MULTILATERAL_BODY', label: 'Multilateral Body' },
      { value: 'INTERNATIONAL_CONSERVATION_ORGANIZATION', label: 'International Conservation Organization' },
      { value: 'INTERNATIONAL_FINANCIAL_INSTITUTION', label: 'International Financial Institution' },
    ],
  },
  {
    label: 'Fallback',
    types: [
      { value: 'OTHER', label: 'Other' },
    ],
  },
];

// Flat list for backward compatibility
export const PERPETRATOR_TYPES = PERPETRATOR_TYPE_GROUPS.flatMap(g => g.types);

export const OTHER_VIOLATION_TYPES = [
  { value: 'ARBITRARY_DETENTION', label: 'Arbitrary Detention' },
  { value: 'CULTURAL_DESTRUCTION', label: 'Cultural Destruction' },
  { value: 'ENFORCED_DISAPPEARANCE', label: 'Enforced Disappearance' },
  { value: 'ENVIRONMENTAL_DESTRUCTION', label: 'Environmental Destruction' },
  { value: 'FORCED_DISPLACEMENT', label: 'Forced Displacement / Eviction' },
  { value: 'KIDNAPPING', label: 'Kidnapping' },
  { value: 'LAND_GRABBING', label: 'Land Grabbing' },
  { value: 'ONLINE_ATTACKS', label: 'Online Attacks (Smear Campaign, Misinformation)' },
  { value: 'PHYSICAL_INJURY', label: 'Physical Injury / Beating / Torture' },
  { value: 'SEXUAL_VIOLENCE', label: 'Sexual Violence and Gender-Based Violence' },
  { value: 'THREATS_AND_INTIMIDATION', label: 'Threats and Intimidation' },
  { value: 'VILIFICATION', label: 'Vilification' },
  { value: 'FPIC_VIOLATION', label: 'Violation of FPIC (Free, Prior and Informed Consent)' },
  { value: 'LOSS_OF_LIVELIHOODS', label: 'Loss of Livelihoods' },
  { value: 'OTHER', label: 'Other' },
];

export const WHO_FILED_ACCUSATIONS_OPTIONS = [
  { value: 'NATIONAL_GOVERNMENT_OFFICIAL', label: 'National-level government official' },
  { value: 'FEDERAL_GOVERNMENT_OFFICIAL', label: 'Federal-level government official' },
  { value: 'LOCAL_GOVERNMENT_OFFICIAL', label: 'Local government official' },
  { value: 'JUDICIARY_OFFICIAL', label: 'Official in the judiciary' },
  { value: 'PRISON_OFFICIAL', label: 'Prison official' },
  { value: 'POLICE', label: 'Police' },
  { value: 'ARMED_FORCES', label: 'Armed forces' },
  { value: 'INTELLIGENCE_AGENCY', label: 'Intelligence agency; secret service' },
  { value: 'VIGILANTE_GROUPS', label: 'Vigilante groups; death squads' },
  { value: 'GUERILLA_ARMIES_OFFICIALS', label: 'Guerilla armies officials' },
  { value: 'NATIONAL_LOCAL_CORPORATION', label: 'National or local corporation' },
  { value: 'TRANSNATIONAL_CORPORATION', label: 'Transnational corporation' },
  { value: 'OTHER', label: 'Other' },
];

export const DEVELOPMENT_PROJECT_TYPES = [
  { value: 'CONSERVATION', label: 'Conservation (Protected Area Designation, Wildlife, Water, Reforestation and Others)' },
  { value: 'ENERGY', label: 'Energy (Non-renewable Resources e.g., fossil fuels, coal, Uranium, Fracking, and others)' },
  { value: 'TRANSITION_ENERGY', label: 'Transition Energy (Solar, Wind, Hydro, Biomass, Geothermal, and others)' },
  { value: 'MINING', label: 'Mining (gold, silver, diamonds, sands, and other metals and minerals)' },
  { value: 'TRANSITION_MINERAL_MINING', label: 'Transition Mineral Mining (critical minerals including cobalt, lithium, copper, nickel, and others)' },
  { value: 'AGRIBUSINESS', label: 'Agribusiness (corporate, monocrops, plantations, and others)' },
  { value: 'TOURISM', label: 'Tourism (Ecotourism, and others)' },
  { value: 'INFRASTRUCTURE', label: 'Infrastructure (roads, urbanization, water systems, telecommunications, and others)' },
  { value: 'DEFORESTATION', label: 'Miscellaneous Projects Causing Deforestation (Logging, Manufacturing, and others)' },
  { value: 'NO_INFORMATION', label: 'No Information' },
];

export const AGE_BRACKETS = [
  { value: 'A', label: 'A (0-12 years old)' },
  { value: 'B', label: 'B (13-25 years old)' },
  { value: 'C', label: 'C (26-35 years old)' },
  { value: 'D', label: 'D (36-59 years old)' },
  { value: 'E', label: 'E (60 years old and above)' }
];

export const DEPENDENT_TYPES = [
  { value: 'WD', label: 'WD (With Dependents)' },
  { value: 'WOD', label: 'WOD (Without Dependents)' },
  { value: 'ND', label: 'ND (No Data)' }
];

export const CONFIDENTIALITY_LEVELS = [
  { value: 'PUBLIC', label: 'Public' },
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'RESTRICTED', label: 'Restricted' },
];

export const DOCUMENT_CATEGORIES = [
  { value: 'EVD', label: 'Evidence' },
  { value: 'RPT', label: 'Report' },
  { value: 'PHT', label: 'Photo' },
  { value: 'VID', label: 'Video' },
  { value: 'AUD', label: 'Audio' },
  { value: 'MAP', label: 'Map' },
  { value: 'LGL', label: 'Legal' },
  { value: 'OTH', label: 'Other' },
];

export const STEP_LABELS = [
  'Documenter Details',
  'Data Sharing Agreement',
  'Incident Details',
  'Categories of HR Violations',
  'Victim Details',
  'Impact of HR Violations',
  'Related Development Projects',
  'Additional Information'
];

export const getViolationTypeLabel = (type: string) => {
  return type.split('_').map(word =>
    word.charAt(0) + word.slice(1).toLowerCase()
  ).join(' ');
};

// ---------------------------------------------------------------------------
// Global violation color scheme — single source of truth for violation colors
// across the whole app (form badges, victim pills, case detail). Keys are the
// flat violation keys: top-level types (KILLING/CRIMINALIZATION) and other-types.
// Full static class strings so Tailwind's content scanner keeps them.
// `solid` = filled/selected state, `soft` = light/read-only badge.
// ---------------------------------------------------------------------------
export const VIOLATION_COLORS: Record<string, { solid: string; soft: string }> = {
  KILLING:                   { solid: 'bg-red-600 text-white border-red-600',         soft: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-800' },
  CRIMINALIZATION:           { solid: 'bg-orange-500 text-white border-orange-500',   soft: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-800' },
  ARBITRARY_DETENTION:       { solid: 'bg-amber-500 text-white border-amber-500',     soft: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900 dark:text-amber-200 dark:border-amber-800' },
  PHYSICAL_INJURY:           { solid: 'bg-rose-500 text-white border-rose-500',       soft: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900 dark:text-rose-200 dark:border-rose-800' },
  KIDNAPPING:                { solid: 'bg-purple-600 text-white border-purple-600',   soft: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900 dark:text-purple-200 dark:border-purple-800' },
  ENFORCED_DISAPPEARANCE:    { solid: 'bg-violet-600 text-white border-violet-600',   soft: 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900 dark:text-violet-200 dark:border-violet-800' },
  SEXUAL_VIOLENCE:           { solid: 'bg-pink-600 text-white border-pink-600',       soft: 'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900 dark:text-pink-200 dark:border-pink-800' },
  ONLINE_ATTACKS:            { solid: 'bg-sky-500 text-white border-sky-500',         soft: 'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900 dark:text-sky-200 dark:border-sky-800' },
  FORCED_DISPLACEMENT:       { solid: 'bg-blue-600 text-white border-blue-600',       soft: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800' },
  LAND_GRABBING:             { solid: 'bg-yellow-500 text-white border-yellow-500',   soft: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-800' },
  ENVIRONMENTAL_DESTRUCTION: { solid: 'bg-green-600 text-white border-green-600',     soft: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-800' },
  CULTURAL_DESTRUCTION:      { solid: 'bg-teal-600 text-white border-teal-600',       soft: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900 dark:text-teal-200 dark:border-teal-800' },
  VILIFICATION:              { solid: 'bg-fuchsia-600 text-white border-fuchsia-600', soft: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200 dark:bg-fuchsia-900 dark:text-fuchsia-200 dark:border-fuchsia-800' },
  THREATS_AND_INTIMIDATION:  { solid: 'bg-cyan-600 text-white border-cyan-600',       soft: 'bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-900 dark:text-cyan-200 dark:border-cyan-800' },
  LOSS_OF_LIVELIHOODS:       { solid: 'bg-lime-600 text-white border-lime-600',       soft: 'bg-lime-100 text-lime-800 border-lime-200 dark:bg-lime-900 dark:text-lime-200 dark:border-lime-800' },
  FPIC_VIOLATION:            { solid: 'bg-indigo-600 text-white border-indigo-600',   soft: 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900 dark:text-indigo-200 dark:border-indigo-800' },
  OTHER:                     { solid: 'bg-gray-500 text-white border-gray-500',       soft: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600' },
};

// Neutral (unselected) pill/badge style — faded, brightens on hover.
export const NEUTRAL_BADGE = 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 opacity-60 hover:opacity-100 hover:bg-gray-50 dark:hover:bg-gray-700';

export const getViolationColor = (key: string) => VIOLATION_COLORS[key] || VIOLATION_COLORS.OTHER;
